import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  IconButton,
  TextField,
 Chip,
  Box,
  Fade,CircularProgress,
} from "@mui/material";

import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import axiosApi from "../Utils/axiosApi";
import { useNavigate } from "react-router-dom";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";


export default function AIChatWidget({
  propertyTypes = ["HOSTEL", "PG"],
}) {

  const isGuestOpsy =
    propertyTypes.includes("HOTEL") ||
    propertyTypes.includes("HOMESTAY") ||
    propertyTypes.includes("RESORT");

 

  const shortPropertyLabel = isGuestOpsy
    ? "hotels, homestays, resorts and PGs"
    : "hostels and PGs";

  const [open, setOpen] = useState(false);
const [message, setMessage] = useState("");
const [loading, setLoading] = useState(false);
const [pendingSearch, setPendingSearch] = useState(null);
const [waitingForDates, setWaitingForDates] = useState(false);
const [messages, setMessages] = useState([
  {
    sender: "ai",
    text: `👋 Hi! I can help you find ${shortPropertyLabel}.

💡 Try: ${
      isGuestOpsy
        ? "Hotel in Kochi with WiFi under ₹2000"
        : "Girls hostel in Kochi with WiFi under ₹800"
    }`,
  },
]);

const navigate = useNavigate();
const messagesEndRef = useRef(null);
const chatRef = useRef(null);
const autoScroll = useRef(true);
const lastMessageCount = useRef(0);


useEffect(() => {

  if (!autoScroll.current) return;

  if (messages.length > lastMessageCount.current) {

    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    lastMessageCount.current = messages.length;
  }

}, [messages]);

const handleScroll = () => {

  const el = chatRef.current;

  if (!el) return;

  autoScroll.current =
    el.scrollHeight - el.scrollTop - el.clientHeight < 100;

};

const handleSend = async () => {
  if (!message.trim()) return;

  const userMessage = message;

  setMessages(prev => [
    ...prev,
    {
      sender: "user",
      text: userMessage,
    },
  ]);

  setMessage("");
  setLoading(true);

  try {

    const requestBody = waitingForDates
      ? {
          prompt: userMessage,
          previousFilters: pendingSearch,
		  propertyTypes,
        }
      : {
          prompt: userMessage,
		  propertyTypes,
        };

    const response = await axiosApi.post(
      "/public/AIPropertySearch",
      requestBody
    );

    // Merge previous filters with new response
    const apiData = response.data;

    const data = {
      ...pendingSearch,
      ...apiData,

      gender: apiData.gender || pendingSearch?.gender,
      location: apiData.location || pendingSearch?.location,
      hostelType: apiData.hostelType || pendingSearch?.hostelType,
      hostelName: apiData.hostelName || pendingSearch?.hostelName,

      maxPrice:
        apiData.maxPrice && apiData.maxPrice > 0
          ? apiData.maxPrice
          : pendingSearch?.maxPrice,

      wifi: apiData.wifi || pendingSearch?.wifi,
      gym: apiData.gym || pendingSearch?.gym,
      parking: apiData.parking || pendingSearch?.parking,
      laundry: apiData.laundry || pendingSearch?.laundry,
      food: apiData.food || pendingSearch?.food,

      searchCheckInDate:
        apiData.searchCheckInDate || pendingSearch?.searchCheckInDate,

      searchCheckOutDate:
        apiData.searchCheckOutDate || pendingSearch?.searchCheckOutDate,
    };

    // Keep merged filters
    setPendingSearch(data);

    // Ask for check-in date if missing
    if (!data.searchCheckInDate) {

      setWaitingForDates(true);

      setMessages(prev => [
        ...prev,
        {
          sender: "ai",
          text: `📅 When would you like to check in to your ${
  isGuestOpsy ? "stay" : "hostel or PG"
}?`,
        },
      ]);

      setLoading(false);
      return;
    }

    setWaitingForDates(false);

    let summary = "I found these filters:\n";

    if (data.gender)
      summary += `👤 ${data.gender}\n`;

    if (data.location)
      summary += `📍 ${data.location}\n`;

    if (data.hostelName)
      summary += `🏠 ${data.hostelName}\n`;

    if (data.maxPrice)
      summary += `💰 Under ₹${data.maxPrice}\n`;

    if (data.food === true || data.food === "true" || data.food === "YES")
      summary += "🍛 Food Included\n";

    if (data.wifi === true || data.wifi === "true" || data.wifi === "YES")
      summary += "📶 WiFi\n";

    if (data.gym === true || data.gym === "true" || data.gym === "YES")
      summary += "🏋️ Gym\n";

    if (data.parking === true || data.parking === "true" || data.parking === "YES")
      summary += "🚗 Parking\n";

    if (data.laundry === true || data.laundry === "true" || data.laundry === "YES")
      summary += "🧺 Laundry\n";

    if (data.searchCheckInDate)
      summary += `📅 Check-in : ${data.searchCheckInDate}\n`;

    if (data.searchCheckOutDate)
      summary += `📅 Check-out : ${data.searchCheckOutDate}\n`;

    setMessages(prev => [
      ...prev,
      {
        sender: "ai",
        text: summary,
      },
    ]);

    const params = new URLSearchParams();

    if (data.hostelName)
      params.set("searchText", data.hostelName);
    else if (data.location)
      params.set("searchText", data.location);

    if (data.gender)
      params.set("gender", data.gender);

    if (data.hostelType)
      params.set("preference", data.hostelType);

    if (data.maxPrice)
      params.set("maxPrice", data.maxPrice);

    if (data.food === true || data.food === "true" || data.food === "YES")
      params.set("food", "true");

    if (data.wifi === true || data.wifi === "true" || data.wifi === "YES")
      params.set("wifi", "true");

    if (data.gym === true || data.gym === "true" || data.gym === "YES")
      params.set("gym", "true");

    if (data.parking === true || data.parking === "true" || data.parking === "YES")
      params.set("parking", "true");

    if (data.laundry === true || data.laundry === "true" || data.laundry === "YES")
      params.set("laundry", "true");

    if (data.searchCheckInDate)
      params.set("searchCheckInDate", data.searchCheckInDate);

    if (data.searchCheckOutDate)
      params.set("searchCheckOutDate", data.searchCheckOutDate);

    setTimeout(() => {
      navigate(`/hostel?${params.toString()}&ai=true`);
      setOpen(false);
    }, 1500);

  } catch (err) {

    setMessages(prev => [
      ...prev,
      {
        sender: "ai",
        text: "Sorry, I couldn't understand your request.",
      },
    ]);

  } finally {

    setLoading(false);

  }
};
  return (
    <>
      {/* Floating Ask AI Button */}

      {!open && (
        <Fade in={!open}>
          <Paper
            elevation={10}
            onClick={() => setOpen(true)}
          sx={{
  position: "fixed",
  right: 24,
  bottom: 24,
  zIndex: 9999,

  display: "flex",
  alignItems: "center",
  gap: 1.5,

  px: 2.2,
  py: 1.4,

  borderRadius: "50px",
  cursor: "pointer",

  background: "#FFFFFF",
  color: "#111827",

  border: "1px solid #E5E7EB",

  boxShadow: "0 12px 30px rgba(0,0,0,.12)",

  transition: ".3s",

  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 18px 40px rgba(37,99,235,.18)",
  },
}}
          >
            <Box
              sx={{
                position: "absolute",
                top: -8,
                right: -8,

                bgcolor: "#F59E0B",
                color: "#fff",

                fontSize: 10,

                px: 1,
                py: .3,

                borderRadius: "999px",

                fontWeight: 700,
              }}
            >
              AI
            </Box>

            <SmartToyIcon
               sx={{
    fontSize: 34,
    color: "#2563EB",
  }}
            />

            <div>
              <Typography
  sx={{
    fontWeight: 700,
    fontSize: 15,
    color: "#111827",
  }}
>
  Ask AI
</Typography>

             <Typography
  sx={{
    fontSize: 12,
    color: "#6B7280",
  }}
>
  Find your perfect stay
</Typography>
            </div>
          </Paper>
        </Fade>
      )}

      {/* Chat Window */}

      {open && (
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            left: {
  xs: 12,
  sm: "auto",
},

right: {
  xs: 12,
  sm: 24,
},

bottom: {
  xs: 12,
  sm: 24,
},

width: {
  xs: "calc(100% - 24px)",
  sm: 400,
},

height: {
  xs: "70vh",
  sm: 620,
},

maxWidth: 400,


            borderRadius: "20px",
boxShadow: "0 20px 60px rgba(0,0,0,.18)",
            overflow: "hidden",

            display: "flex",
            flexDirection: "column",

            zIndex: 9999,
          }}
        >
          {/* Header */}

          <Box
  sx={{
    height: 90,

background:
"linear-gradient(135deg,#2563EB 0%,#4F46E5 100%)",

color:"#fff",

borderBottom:"1px solid rgba(255,255,255,.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
  }}
>
           <Box display="flex" alignItems="center">

  <Box
    sx={{
      width: 48,
      height: 48,
      borderRadius: "50%",
      background: "#ffffff22",

border:"1px solid rgba(255,255,255,.25)",

boxShadow:"0 4px 12px rgba(0,0,0,.15)",

      display: "flex",
      justifyContent: "center",
      alignItems: "center",

      mr: 2,
    }}
  >
    <SmartToyIcon sx={{ color: "#fff", fontSize: 28 }} />
  </Box>

  <Box>
  <Typography
    sx={{
      fontWeight: 700,
      fontSize: 18,
      color: "#FFFFFF",
      letterSpacing: "0.3px",
    }}
  >
    AI Assistant
  </Typography>

  <Typography
    sx={{
      fontSize: 13,
      color: "rgba(255,255,255,0.85)",
      fontWeight: 400,
    }}
  >
    Find your perfect {isGuestOpsy ? "stay" : "hostel or PG"}
  </Typography>
</Box>

</Box>

          <IconButton
  onClick={() => setOpen(false)}
  sx={{
    color: "#fff",
    bgcolor: "rgba(255,255,255,.12)",
    width: 40,
    height: 40,

    "&:hover": {
      bgcolor: "rgba(255,255,255,.25)",
    },
  }}
>
  <CloseIcon />
</IconButton>
          </Box>

          {/* Chat */}

  <Box
  ref={chatRef}
  onScroll={handleScroll}
  sx={{
    flex: 1,
    background: "#F9FAFB",
    p: 2,
    overflowY: "auto",
  }}
>
{!waitingForDates && (
<Box
  display="flex"
  flexWrap="wrap"
  gap={1}
  mb={2}
>
  {(
  isGuestOpsy
    ? [
        "Hotel in Kochi",
        "Resort in Kochi",
        "Homestay with WiFi",
        "WiFi",
        "Under ₹2000",
      ]
    : [
        "Girls Hostel",
        "Boys Hostel",
        "PG with Food",
        "WiFi",
        "Kochi",
        "Under ₹800",
      ]
).map((item) => (
    <Chip
      key={item}
      label={item}
      clickable
      onClick={() => setMessage(item)}
      sx={{
        bgcolor: "#EEF4FF",
        color: "#2563EB",
        fontWeight: 600,
      }}
    />
  ))}
</Box>
)}
  {messages.map((msg,index)=>(
    <Box
      key={index}
      display="flex"
      justifyContent={
        msg.sender==="user"
          ? "flex-end"
          : "flex-start"
      }
      mb={2}
    >
      <Paper
        sx={{
          p:2,
          maxWidth:
  msg.sender==="user"
    ? "75%"
    : "90%",
          bgcolor:
   msg.sender === "user"
    ? "#60A5FA"
    : "#F8FAFF",

border:
  msg.sender==="user"
    ? "none"
    : "1px solid #DCEBFF",

boxShadow:
  msg.sender==="user"
    ? "none"
    : "0 2px 8px rgba(37,99,235,.08)",
	
          color:
            msg.sender==="user"
            ? "#fff"
            : "#111",
          borderRadius:3
        }}
      >
        <Typography
          sx={{
            whiteSpace:"pre-line"
          }}
        >
          {msg.text}
        </Typography>
      </Paper>
    </Box>
  ))}

  {loading && (

    <Box display="flex">

      <Paper
        sx={{
          p:2,
          borderRadius:3
        }}
      >

        <Box
          display="flex"
          alignItems="center"
          gap={1}
        >
          <CircularProgress size={18}/>
          <Typography>
            🤖 Thinking...
          </Typography>
        </Box>

      </Paper>

    </Box>

  )}
<div ref={messagesEndRef} />
</Box>

          {/* Input */}

        <Box
sx={{
background:"#fff",

borderTop:"1px solid #E5E7EB",

display:"flex",

alignItems:"center",

p:2,

gap:1,
}}
>
{waitingForDates && (
  <Typography
    sx={{
      fontSize: 12,
      color: "#2563EB",
      fontWeight: 600,
      mb: 1,
    }}
  >
    📅 Please enter your check-in date to continue.
  </Typography>
)}
<TextField
  size="small"
  fullWidth
  type={waitingForDates ? "date" : "text"}
  value={message}
 placeholder={
  waitingForDates
    ? "Select check-in date"
    : `Describe your ${isGuestOpsy ? "stay" : "hostel or PG"}...`
}
  onChange={(e) => setMessage(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  }}
  InputLabelProps={
    waitingForDates
      ? { shrink: true }
      : undefined
  }
/>
      <IconButton  disabled={loading}
  onClick={handleSend}
  sx={{
    width: 44,
    height: 44,
    background: "linear-gradient(135deg,#2563EB,#4F46E5)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg,#1D4ED8,#4338CA)",
    },
  }}
>
  {waitingForDates ? <CalendarMonthIcon /> : <SendIcon />}
</IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
}